import React from "react";
import { Link } from "react-router-dom";
import PageListController from "../../controllers/PageListController";
import { generatePath } from "react-router";
import { Routes } from "../routes";
import useCurrentUser from "../../hooks/useCurrentUser";

/**
 * LinkPage now supports selecting a page scoped to a specific commanderie when multiple pages
 * share the same identifier. If `commanderieChapitre` is provided, the component will choose
 * the page whose `commanderie` matches this value. If none matches, it will ignore other pages
 * and fall back to `fallbackIdentifier` (global), without applying any commanderie constraint.
 */
const LinkPage = ({
    identifier,
    fallbackIdentifier,
    commanderieChapitre,
    renderPrefix,
    multiple,
    children,
    ...props
}) => {
    const currentUser = useCurrentUser();
    return (
        <PageListController
            filter={{
                identifier: identifier,
            }}
            withLoader={false}
        >
            {({ loaded, error, data: pages }) => {
                const hasPages =
                    loaded &&
                    !error &&
                    Array.isArray(pages) &&
                    pages.length > 0;

                // Multiple mode: render one link per eligible page (per user's commanderies)
                if (multiple) {
                    if (!hasPages) return null;
                    const codes = Array.isArray(currentUser?.codesCommanderies)
                        ? currentUser.codesCommanderies.map((c) => String(c))
                        : [];
                    if (codes.length === 0) return null;
                    const items = pages
                        .filter(
                            (p) =>
                                p &&
                                p.commanderie != null &&
                                codes.includes(String(p.commanderie))
                        )
                        .sort((a, b) =>
                            String(a.menuTitle || "").localeCompare(
                                String(b.menuTitle || "")
                            )
                        );
                    let any = false;
                    const nodes = items.map((page) => {
                        const hasChildren =
                            children !== undefined && children !== null;
                        const title = hasChildren ? children : page.menuTitle;
                        const titleStr =
                            typeof title === "string" ? title.trim() : null;
                        if (
                            !hasChildren &&
                            (!titleStr || titleStr.length === 0)
                        ) {
                            return null;
                        }
                        any = true;
                        return (
                            <React.Fragment key={page.id}>
                                {renderPrefix && renderPrefix()}
                                <Link
                                    to={generatePath(Routes.catchAll, {
                                        slug: page.slug || "m",
                                        id: page.id,
                                    })}
                                    {...props}
                                >
                                    {hasChildren ? children : titleStr}
                                </Link>
                            </React.Fragment>
                        );
                    });
                    return any ? <>{nodes}</> : null;
                }

                const pickPage = () => {
                    if (!hasPages) return null;
                    if (commanderieChapitre != null) {
                        // Match the page tied to the target commanderie (normalize types for strict equality)
                        const match = pages.find(
                            (p) =>
                                p &&
                                p.commanderie != null &&
                                String(p.commanderie) ===
                                    String(commanderieChapitre)
                        );
                        if (match) return match;
                        return null; // No match → let fallback handle it
                    }
                    // No specific commanderie requested → keep previous behavior (first page)
                    return pages[0];
                };
                const page = pickPage();
                if (page) {
                    const hasChildren =
                        children !== undefined && children !== null;
                    const title = hasChildren ? children : page.menuTitle;
                    const titleStr =
                        typeof title === "string" ? title.trim() : null;
                    if (!hasChildren && (!titleStr || titleStr.length === 0)) {
                        // Title is empty/whitespace-only and no explicit children provided → skip rendering
                        return null;
                    }
                    return (
                        <>
                            {renderPrefix && renderPrefix()}
                            <Link
                                to={generatePath(Routes.catchAll, {
                                    slug: page.slug || "m",
                                    id: page.id,
                                })}
                                {...props}
                            >
                                {hasChildren ? children : titleStr}
                            </Link>
                        </>
                    );
                }
                if (loaded && !error && fallbackIdentifier) {
                    // Fallback is global: do not filter by commanderie
                    return (
                        <PageListController
                            filter={{ identifier: fallbackIdentifier }}
                            withLoader={false}
                        >
                            {({
                                loaded: fLoaded,
                                error: fError,
                                data: fPages,
                            }) => {
                                if (
                                    fLoaded &&
                                    !fError &&
                                    Array.isArray(fPages) &&
                                    fPages[0]
                                ) {
                                    const fPage = fPages[0];
                                    const hasChildren =
                                        children !== undefined &&
                                        children !== null;
                                    const title = hasChildren
                                        ? children
                                        : fPage.menuTitle;
                                    const titleStr =
                                        typeof title === "string"
                                            ? title.trim()
                                            : null;
                                    if (
                                        !hasChildren &&
                                        (!titleStr || titleStr.length === 0)
                                    ) {
                                        return null;
                                    }
                                    return (
                                        <>
                                            {renderPrefix && renderPrefix()}
                                            <Link
                                                to={generatePath(
                                                    Routes.catchAll,
                                                    {
                                                        slug: fPage.slug || "m",
                                                        id: fPage.id,
                                                    }
                                                )}
                                                {...props}
                                            >
                                                {hasChildren
                                                    ? children
                                                    : titleStr}
                                            </Link>
                                        </>
                                    );
                                }
                                return null;
                            }}
                        </PageListController>
                    );
                }
                return null;
            }}
        </PageListController>
    );
};
export default LinkPage;
